// The parts of the engine a dispatcher touches, typed.
//
// `@wayfind/core` types the *viewer's* half of the thread channel, which
// is all the customer app needs. A console lives on the other side of
// §20 and §21: it mints plans, seals tickets to devices it has enrolled,
// signs offers, and derives a route's day keys. None of that has a
// hand-written type in core, and adding one there would put the
// dispatcher's vocabulary in a package the customer app also imports.
//
// So the shapes live here, structurally, over the same bundle core
// already loads. Where a name differs from pulsemesh-threads.md the
// document wins and the difference is a bug in this file.

import type { EngineBundles, LatLon, RouteEngine } from "@wayfind/core";

/** §20.2 — a plan stop as the encoder wants it, metadata and all. */
export interface PlanStopInput extends LatLon {
  label?: string;
  plannedUnixSeconds?: number | null;
  /** §20.8, all four optional. Null and "" both mean *unstated*. */
  orderRef?: string | null;
  parcels?: number | null;
  instructions?: string | null;
  contact?: string | null;
}

export interface PlanInput {
  stops: PlanStopInput[];
  dwellSeconds?: number;
  travelMode?: number;
}

export interface DecodedPlanStop extends LatLon {
  index: number;
  label: string;
  plannedUnixSeconds: number | null;
  orderRef: string | null;
  parcels: number | null;
  instructions: string | null;
  contact: string | null;
}

export interface DecodedPlan {
  version: number;
  travelMode: number;
  dwellSeconds: number;
  stops: DecodedPlanStop[];
  planRef: Uint8Array;
  bytes: Uint8Array;
}

export interface DeviceCard {
  version: number;
  publicKey: Uint8Array;
  name: string;
  fingerprint: string;
  bytes: Uint8Array;
}

export interface SeedCard {
  version: number;
  addresses: string[];
  label: string;
  bytes: Uint8Array;
}

export interface DecodedTicket {
  mode: number;
  epochPrefix8: Uint8Array;
  notAfter: number;
  issuerPublicKey: Uint8Array;
  planBytes: Uint8Array;
  plan: DecodedPlan;
  privateSeed: Uint8Array | null;
  bootstrap: string[];
  /**
   * §21.11 — present on a **route-day ticket**, and what makes it one.
   * The run's identity is read out of here rather than off `privateSeed`,
   * which is the day key and must never become a link.
   */
  dayCertificate: DayCertificate | null;
  routeDay: boolean;
  serviceDay: number | null;
  bytes: Uint8Array;
}

export interface IssuedSealedTicket {
  sealed: Uint8Array;
  base64url: string;
  driverUrl: string;
  ticket: DecodedTicket;
  link: Uint8Array;
  url: string | null;
  jobIdHex: string;
  bootstrap: string[];
  recipientCount: number;
}

export interface DecodedOffer {
  mode: number;
  travelMode: number;
  notAfter: number;
  jobId: Uint8Array;
  jobIdHex: string;
  planRef: Uint8Array;
  stopCount: number;
  /**
   * The published centroid, snapped to the 0.01° grid it was coarsened
   * onto. There is no precise reading of this to reach for: the value is
   * a multiple of the grid, and the grid is about a kilometre.
   */
  centroid: { lat: number; lon: number; gridDegrees: number };
  spread: number;
  spreadLabel: string;
  totalMeters: number;
  payMinor: number | null;
  currency: string | null;
  label: string | null;
  issuerPublicKey: Uint8Array;
  bytes: Uint8Array;
}

export interface IssuedOffer {
  bytes: Uint8Array;
  base64url: string;
  offer: DecodedOffer;
  jobIdHex: string;
  url: string;
}

export interface DayCertificate {
  rootPublicKey: Uint8Array;
  dayPublicKey: Uint8Array;
  serviceDay: number;
  notBefore: number;
  notAfter: number;
  bytes: Uint8Array;
}

export interface MintedDay {
  daySeed: Uint8Array;
  certificate: DayCertificate;
  rootPublicKey: Uint8Array;
  dayPublicKey: Uint8Array;
}

/** The dispatcher's half of `pulsemesh-threads.browser.js`. */
export interface DispatchBundle {
  /**
   * The size of a v2 follow link, stated by the library rather than
   * assumed here: it grew once already, when the capability secret moved
   * into the link beside the verification key.
   */
  LINK_BYTES: number;
  THREAD_MODE: { COARSE: number; FINE: number };
  OFFER_SPREAD: readonly { readonly bucket: number; readonly maxMeters: number | null; readonly label: string }[];

  bytesToBase64Url(bytes: Uint8Array): string;
  base64UrlToBytes(text: string): Uint8Array;

  encodeThreadPlan(plan: PlanInput): Uint8Array;
  decodeThreadPlan(bytes: Uint8Array): DecodedPlan;
  planRefOf(planBytes: Uint8Array): Uint8Array;

  generateThreadKeypair(seed?: Uint8Array | null): Promise<{
    privateSeed: Uint8Array;
    publicKey: Uint8Array;
  }>;
  publicKeyFromSeed(seed: Uint8Array): Promise<Uint8Array>;
  /**
   * §20.7 — opens one sealed doorstep photo. The key derives from the
   * run's private seed and the stop it was taken at, so this works on
   * the driver's phone and on the console that minted the ticket, and
   * nowhere else. Throws when the bytes do not match the commitment the
   * driver signed, which is the check that makes a photo evidence.
   */
  openPhoto(sealed: Uint8Array, params: {
    privateSeed: Uint8Array;
    planRef: Uint8Array;
    stopIndex: number;
    hash: string;
  }): Promise<Uint8Array>;
  /**
   * Capability material from a root seed, without reusing its Ed25519
   * public key as a symmetric secret. What a v2 link carries beside the
   * verification key.
   */
  deriveThreadSecret(rootSeed: Uint8Array): Promise<Uint8Array>;

  generateDeviceKeypair(seed?: Uint8Array | null): Promise<{
    privateKey: Uint8Array;
    publicKey: Uint8Array;
  }>;
  fingerprint(publicKey: Uint8Array): string;
  encodeDeviceCard(card: { publicKey: Uint8Array; name?: string }): Uint8Array;
  deviceCardUrl(card: Uint8Array | { publicKey: Uint8Array; name?: string }, baseUrl?: string): string;
  parseDeviceCardUrl(url: string): DeviceCard;
  /** The pairing offer as the URL its QR encodes (§16.1). */
  pairingOfferUrl(bytes: Uint8Array, baseUrl?: string): string;

  encodeSeedCard(card: { addresses: string[]; label?: string }): Uint8Array;
  seedCardUrl(card: Uint8Array | { addresses: string[]; label?: string }, baseUrl?: string): string;
  parseSeedCardUrl(url: string): SeedCard;
  isDialableAddress(address: string): boolean;

  issueSealedTicket(params: {
    issuerSeed: Uint8Array;
    epoch32?: Uint8Array | null;
    epochPrefix8?: Uint8Array | null;
    plan?: PlanInput | null;
    planBytes?: Uint8Array | null;
    notAfter: number;
    mode?: number;
    privateSeed?: Uint8Array | null;
    bootstrap?: string[] | null;
    /**
     * §21.11 — makes this a route-day ticket. `issuerSeed` is then the
     * route **root** seed, `privateSeed` is **required** and is the
     * CSPRNG day seed `mintDayCertificate` returned — it is not
     * derivable, which is the point — and `link` comes back as the
     * **term's** link.
     */
    dayCertificate?: DayCertificate | Uint8Array | null;
    baseUrl?: string | null;
    recipients: (Uint8Array | { publicKey: Uint8Array })[];
  }): Promise<IssuedSealedTicket>;

  /**
   * The one place a ticket's 78 bytes come from. On a route-day ticket it
   * reads the identity out of the certificate and returns the route's own
   * v2 link, so there is no arm of the library that can produce a link
   * over a day key (§21.11).
   */
  ticketFollowLink(ticket: DecodedTicket | Uint8Array): Promise<Uint8Array>;

  /** §20.5 handover: open what this console sealed, seal it to somebody else. */
  openSealedTicket(sealed: Uint8Array, devicePrivateKey: Uint8Array): Promise<Uint8Array>;
  sealTicket(
    ticketBytes: Uint8Array,
    recipients: (Uint8Array | { publicKey: Uint8Array })[]
  ): Promise<Uint8Array>;
  sealedTicketUrl(sealed: Uint8Array, baseUrl?: string): string;
  decodeThreadTicket(bytesOrBase64url: Uint8Array | string): DecodedTicket;

  issueJobOffer(params: {
    issuerSeed: Uint8Array;
    ticket?: DecodedTicket | null;
    epoch32?: Uint8Array | null;
    epochPrefix8?: Uint8Array | null;
    plan?: PlanInput | null;
    planBytes?: Uint8Array | null;
    notAfter?: number | null;
    mode?: number;
    totalMeters?: number;
    payMinor?: number | null;
    currency?: string | null;
    label?: string | null;
    baseUrl?: string | null;
  }): Promise<IssuedOffer>;
  decodeJobOffer(bytesOrBase64url: Uint8Array | string): DecodedOffer;
  awardMatchesOffer(offer: DecodedOffer, ticket: DecodedTicket): { ok: boolean; reason: string | null };

  serviceDayOf(year: number, month: number, day: number): number;
  mintDayCertificate(params: {
    rootSeed: Uint8Array;
    planRef?: Uint8Array | null;
    serviceDay: number;
    notBefore: number;
    notAfter: number;
  }): Promise<MintedDay>;
  routeFollowLink(params: {
    threadSecret: Uint8Array;
    rootPublicKey: Uint8Array;
    epochPrefix8: Uint8Array;
    notAfter: number;
  }): Uint8Array;

  threadLinkUrl(baseUrl: string, link: Uint8Array, options?: { bootstrap?: string[] | null }): string;
  classifyThreadArtifact(value: string | Uint8Array): {
    kind: "ticket" | "offer" | "seed" | "link" | null;
    reason: string | null;
    sealed: boolean;
    /**
     * **`null` is not `false`.** What sort of job is inside a sealed PME1
     * is ciphertext, so a sealed or unreadable ticket answers "not
     * knowable without the key" and a UI that rendered that as "an
     * ordinary job" would be inventing a claim nobody made (§21.11).
     */
    routeDay: boolean | null;
    serviceDay: number | null;
  };
}

/** The route graph, plus the one call a dispatcher makes that a viewer never does. */
export interface DispatchRouteEngine extends RouteEngine {
  itinerary(params: {
    stops: LatLon[];
    openEnd?: boolean;
    roundTrip?: boolean;
    geometry?: boolean;
  }): Promise<{ order: number[]; totalSeconds: number; totalMeters: number }>;
}

export function dispatchBundle(bundles: EngineBundles): DispatchBundle {
  return bundles.threads as unknown as DispatchBundle;
}

export function dispatchEngine(engine: RouteEngine | null): DispatchRouteEngine | null {
  return engine as DispatchRouteEngine | null;
}

/** Hex, both directions. JSON has no byte array and localStorage has no JSON. */
export function toHex(bytes: Uint8Array): string {
  let out = "";
  for (const byte of bytes) out += byte.toString(16).padStart(2, "0");
  return out;
}

export function fromHex(text: string): Uint8Array {
  const clean = text.trim();
  const out = new Uint8Array(clean.length >> 1);
  for (let i = 0; i < out.length; i++) out[i] = Number.parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  return out;
}
